import { Injectable } from '@angular/core';
import { ApplicationContext, SourceSheet, TableConfig } from 'src/app/model';
import { ExcelDataMapperService } from './excel-data-mapper.service';
import { ApplicationInsightsService } from '../application-insights/application-insights.service';
import { HttpService } from '../http-service/http.service';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from 'src/app/config/api-endpoints';
import { environment } from 'src/environments/environment';
import { WOKRPAPER_SHEETS } from 'src/app/constants/workbook.constants';
import { SaveHeadersProvider } from './save-headers-provider.interface';

@Injectable({
  providedIn: 'root'
})
export class CreditCarryForwardDataService implements SaveHeadersProvider {
  private isConfigLoaded = false;
  private config: TableConfig = {
    sheetType: SourceSheet.CreditCarryForward,
    columnMappings: [],
  };
  private readonly entityColumnMappings = [
    { Key: "State", DisplayName: "State", Column: "A" },
    { Key: "EntityName", DisplayName: "Entity", Column: "B" },
    { Key: "VertexCompanyCode", DisplayName: "Vertex Company Code", Column: "C" },
    { Key: "Jurisdiction", DisplayName: "Jurisdiction", Column: "D" },
    { Key: "Notes", DisplayName: "Notes", Column: "E" },
    { Key: "CreditCarryForwardDate", DisplayName: "Credit Carry Forward Date", Column: "F" },
    { Key: "CurrentMonth", DisplayName: "Current Month", Column: "H" },
    { Key: "PriorYears", DisplayName: "Prior Years", Column: "J" },
    { Key: "Jan", DisplayName: "JAN", Column: "L" },
    { Key: "Feb", DisplayName: "FEB", Column: "M" },
    { Key: "Mar", DisplayName: "MAR", Column: "N" },
    { Key: "Apr", DisplayName: "APR", Column: "O" },
    { Key: "May", DisplayName: "MAY", Column: "P" },
    { Key: "Jun", DisplayName: "JUN", Column: "Q" },
    { Key: "Jul", DisplayName: "JUL", Column: "R" },
    { Key: "Aug", DisplayName: "AUG", Column: "S" },
    { Key: "Sep", DisplayName: "SEP", Column: "T" },
    { Key: "Oct", DisplayName: "OCT", Column: "U" },
    { Key: "Nov", DisplayName: "NOV", Column: "V" },
    { Key: "Dec", DisplayName: "DEC", Column: "W" },
  ];

  constructor(
    private excelDataMapperService: ExcelDataMapperService,
    private appInsights: ApplicationInsightsService,
    private http: HttpService
  ) { }

  private loadConfig() {
    this.appInsights.trackEvent("CreditCarryForwardDataService.loadConfig.called");
    try {
      if (!this.isConfigLoaded) {
        this.appInsights.trackEvent("CreditCarryForwardDataService.loadConfig.settingSheetName", { sheetName: "Credit Carry Forward" });
        this.appInsights.trackEvent("CreditCarryForwardDataService.loadConfig.settingTableName", { tableName: "CreditCFData" });
        this.appInsights.trackEvent("CreditCarryForwardDataService.loadConfig.settingColumnMappings");
        this.config.columnMappings = [...this.entityColumnMappings];
        this.isConfigLoaded = true;
        this.appInsights.trackEvent("CreditCarryForwardDataService.loadConfig.configLoaded");
      }
    } catch (error: any) {
      this.appInsights.trackException(error); this.appInsights.trackException(error, { location: "CreditCarryForwardDataService.loadConfig" });
      this.appInsights.trackEvent("CreditCarryForwardDataService.loadConfig.error", { error: error?.message || error });
      console.error("Error loading config in CreditCarryForwardDataService:", error);
    }
  }

  public getConfig(): TableConfig {
    this.loadConfig();
    return this.config;
  }

  public getData(appContext: ApplicationContext): Observable<any[]> {
      const isRG = !!appContext.reportingGroupId;
      const endpoint = isRG
        ? API_ENDPOINTS.inputSheet.rgCreditCarryForward
        : API_ENDPOINTS.inputSheet.creditCarryForward;
      const url = `${environment.apiBaseURL}${endpoint}`;
      return this.http.post(url, appContext);
  }

  public getSaveHeaders(): string[] {
    return Object.keys(this.mapCreditCarryForward);
  }

  public prepareCreditCarryForwardSheetData(matrix: string[][]): {
    headers: string[];
    rows: (string | number | boolean)[][];
  } {
    const { sqlHeaders, tableRows } = this.excelDataMapperService.buildFilteredData(
      matrix,
      this.mapCreditCarryForward,
      WOKRPAPER_SHEETS.CreditCarryForwardSheet,
      {
        skipRows: 0,
        removeLastRow: true,
        skipRowsWhenAllMappedColumnsBlank: true,
        ignoreBlankCheckColumns: ['Vertex Company Code']
      }
    );

    const notesIndex = sqlHeaders.indexOf('Notes');
    if (notesIndex >= 0) {
      tableRows.forEach((row) => {
        const value = row[notesIndex];
        if (typeof value === 'string' && value.trim() === '') {
          row[notesIndex] = '';
        }
      });
    }

    tableRows.unshift(sqlHeaders);

    return { headers: sqlHeaders, rows: tableRows };
  }

  private mapCreditCarryForward: Record<string, { sqlCol: string; dataType: string; required?: boolean }> = {
    'State': { sqlCol: 'State', dataType: 'string', required: true },
    'Vertex Company Code': { sqlCol: 'VertexCompanyCode', dataType: 'string', required: true },
    'Jurisdiction': { sqlCol: 'Jurisdiction', dataType: 'string', required: true },
    'State Abbreviation': { sqlCol: 'StateAbbreviation', dataType: 'string', required: true },
    'Notes': { sqlCol: 'Notes', dataType: 'string', required: false },
    'Credit Carry Forward Date': { sqlCol: 'CreditCarryForwardDate', dataType: 'datetime', required: false },
    'Current Month': { sqlCol: 'CurrentMonth', dataType: 'number', required: false },
    'Prior Years': { sqlCol: 'PriorYears', dataType: 'number', required: false },
    'Total': { sqlCol: 'Total', dataType: 'number', required: false },
    'Jan': { sqlCol: 'Jan', dataType: 'number', required: false },
    'Feb': { sqlCol: 'Feb', dataType: 'number', required: false },
    'Mar': { sqlCol: 'Mar', dataType: 'number', required: false },
    'Apr': { sqlCol: 'Apr', dataType: 'number', required: false },
    'May': { sqlCol: 'May', dataType: 'number', required: false },
    'Jun': { sqlCol: 'Jun', dataType: 'number', required: false },
    'Jul': { sqlCol: 'Jul', dataType: 'number', required: false },
    'Aug': { sqlCol: 'Aug', dataType: 'number', required: false },
    'Sep': { sqlCol: 'Sep', dataType: 'number', required: false },
    'Oct': { sqlCol: 'Oct', dataType: 'number', required: false },
    'Nov': { sqlCol: 'Nov', dataType: 'number', required: false },
    'Dec': { sqlCol: 'Dec', dataType: 'number', required: false },
  };
  
}

